import { computed, effect, Inject, inject, Injectable, signal } from '@angular/core';
import { combineLatest, concatAll, delay, firstValueFrom, forkJoin, from, map, Observable, ReplaySubject, single, Subscriber } from 'rxjs';
import loadLibrary, { cKopConfig, JSBridge, MainModule } from '@wasm/KopWeb';
import { AITypes } from '../utils/AITypes';
import { SERVO_TYPE_NAME, tServoKeys } from '../props/PropTypes';
import { ServerAPI } from '../serverapi';
import { PcibService } from './pcib.service';
import { KopListService } from './kop-list.service';
import { saveLocalFiles } from '../utils/wasmVector';
import { DialogCloseResult, DialogRef, DialogService } from '@progress/kendo-angular-dialog';

export function makeAvailable(filePath: string, wasmModule: MainModule, electronAPI: ServerAPI) {
  let ret = electronAPI.getFile(filePath).then(data => {
    if (data != undefined) {
      var stream = wasmModule.FS.open('/working/' + filePath, 'w+', undefined);
      wasmModule.FS.write(stream, data, 0, data.length, 0, undefined);
      wasmModule.FS.close(stream);
    }
  });
  return ret;
}



export function loadKop(klantNo: string, wasmModule: MainModule, electronAPI: ServerAPI, historyId: string | undefined = undefined) {
  let ret = electronAPI.loadKop(klantNo, historyId).then(data => {

    if (data.sxbData != undefined) {
      var stream = wasmModule.FS.open(`/working/SXB/${klantNo}.SXB`, 'w+', undefined);
      wasmModule.FS.write(stream, data.sxbData, 0, data.sxbData.length, 0, undefined);
      wasmModule.FS.close(stream);
    }
    if (data.modulesData != undefined) {
      var stream = wasmModule.FS.open(`/working/modules/${klantNo}.modules.json`, 'w+', undefined);
      wasmModule.FS.write(stream, data.modulesData, 0, data.modulesData.length, 0, undefined);
      wasmModule.FS.close(stream);
    }
    return data;
  });
  return ret;
}

@Injectable({
  providedIn: null
})
export class JSBridgeService {

  private jsBridge: ReplaySubject<JSBridge> = new ReplaySubject<JSBridge>(1);
  private jsBridgeSync = signal<JSBridge | undefined>(undefined); //JSBridge | undefined;

  public wasmModule: MainModule | undefined;

  private klantNo: string | undefined;

  public getKlantNo(): string {
    return this.klantNo!;
  }

  public isSercomKlant(): boolean {
    return this.klantNo!.startsWith('SC');
  }

  public changedKlantNo(newKlantNo: string) {
    this.klantNo = newKlantNo;
  }

  public klantNoSub = new ReplaySubject<{ klantNo: string, historyId?: string }>(1);

  public initKlantNo(klantNo: string, historyId?: string) {
    this.klantNo = klantNo.toUpperCase();
    this.klantNoSub.next({ klantNo: this.klantNo, historyId: historyId });
    // this.klantNoSubscriber(klantNo);
  }

  constructor(@Inject('ServerApi') private electronAPI: ServerAPI, private pcibService: PcibService, private kopListService: KopListService, private dialogService: DialogService) {



    let libraryObs = from(loadLibrary()).pipe(delay(100));
    let m1 = combineLatest([libraryObs, this.klantNoSub])
      .subscribe(async ([library, { klantNo, historyId }]) => {
        console.log(library, klantNo, historyId);
        klantNo = klantNo.toUpperCase();
        this.wasmModule = library;
        let f1 = makeAvailable('db/KopData.db', library, this.electronAPI);
        let f2 = makeAvailable('db/KopData_en.db', library, this.electronAPI);
        let f3 = makeAvailable('db/KopTrans.db', library, this.electronAPI);
        let pall = Promise.all([f1, f2, f3]);
        await pall;

        if (historyId != undefined) {
          let data = await loadKop(klantNo, library, this.electronAPI, historyId);
          if (data.modulesData != undefined) {
            let modulesStr = new TextDecoder().decode(data.modulesData); //data.modulesData.toString();
            window.localStorage.setItem(`${klantNo}.modules.json`, modulesStr);
          }
          this.loadFromMemory = true;
        } else if (window.localStorage.getItem(`${klantNo}.SXB`) != null) {
          let dataBase64 = window.localStorage.getItem(`${klantNo}.SXB`);
          let data = atob(dataBase64!).split('').map(function (c) { return c.charCodeAt(0); });
          //let data = atob(window.localStorage.getItem("autosave")!);
          const dialog: DialogRef = this.dialogService.open({
            title: `Lokale versie voor ${klantNo}`,
            content: `U hebt een niet-opgeslagen lokale versie voor ${klantNo}. Wilt u deze gebruiken of overschrijven met de versie van de server?`,
            actions: [{ text: "Server", id: "no" }, { text: "Lokale", id: "yes", themeColor: "primary" }],
            preventAction: (ev) => {
              return ev instanceof DialogCloseResult;
            },
          });

          let result = await (firstValueFrom(dialog.result)) as any;
          if (result.id == "yes") {
            this.loadFromMemory = true;
            var stream = library.FS.open(`/working/SXB/${klantNo}.SXB`, 'w+', undefined);
            if (data != undefined) {
              library.FS.write(stream, data, 0, data.length, 0, undefined);
              library.FS.close(stream);
            }
          } else {
            await loadKop(klantNo, library, this.electronAPI);
          }
        } else {
          await loadKop(klantNo, library, this.electronAPI);
        }

        let bridge = new library.JSBridge(klantNo);
        this.jsBridgeSync.set(bridge);
        let config = bridge.getConfig();
        this.kopConfig.set(config);
        this.version.set(config.klimaatVersion.str.toString());
        // bridge.saveXML(`${this.klantNo}.old.XML`);


        this.servoTypeName = Object.entries(this.wasmModule!.eServoSoort)
          .filter(([k, _]) => SERVO_TYPE_NAME[k as tServoKeys] != undefined)
          .map(([k, v]) => {
            let ret = { type: v.value, text: SERVO_TYPE_NAME[k as tServoKeys]! };
            return ret;
          });

        this.jsBridge.next(bridge);


      });

  }

  public servoTypeName: { type: number, text: string }[] = [];
  public kopConfig = signal<cKopConfig | undefined>(undefined, { equal: (a, b) => false });
  public version = signal<string | undefined>(undefined);

  public isDev = signal<boolean>(true);

  public loadFromMemory: boolean = false;

  public isVersionSupported = computed(() => {
    let v = this.version();
    if (v != undefined) {
      let main = v.split("-")[0];
      if (Number(main) >= 26) {
        return true;
      }
    }
    return false;
  });

  public async retreiveKop(historyId: string | undefined = undefined) {
    let files: Record<string, any> = {};
    if (historyId == undefined) {
      this.addFolder('/working/SXB/', files);

    } else if (this.klantNo != undefined) {
      let tmHistoryId = historyId == 'last' ? undefined : historyId;
      let ret = await this.electronAPI.loadKop(this.klantNo, tmHistoryId);
      files[`/working/SXB/${this.klantNo}.SXB`] = ret.sxbData;
    }

    return files;
  }

  public moveFile(oldPath: string, newPath: string) {
    this.wasmModule!.FS.rename('/working/' + oldPath, '/working/' + newPath);
  }

  public async saveFile(filePath: string) {
    var data = this.wasmModule!.FS.readFile('/working/' + filePath, {
      encoding: 'binary'
    });
    return this.electronAPI.saveFile(filePath, data);
  }


  // public getOLDXML() {
  //   var data = this.wasmModule!.FS.readFile(`${this.klantNo}.old.XML`, { encoding: 'utf8' });
  //   return data;
  // }

  // public getNewXML() {
  //   this.jsBridgeSync()!.saveXML(`${this.klantNo}.new.XML`);
  //   var data = this.wasmModule!.FS.readFile(`${this.klantNo}.new.XML`, { encoding: 'utf8' });
  //   return data;
  // }


  private addFolder(folder: string, files: Record<string, any>) {
    let t = this.wasmModule!.FS.readdir(folder);
    for (let f of t) {
      let fPath = `${folder}${f}`;
      let stat = this.wasmModule!.FS.stat(fPath, undefined);
      let isFile = this.wasmModule!.FS.isFile(stat.mode);
      if (isFile) {
        var data = this.wasmModule!.FS.readFile(fPath, {
          encoding: 'binary'
        });
        files[fPath] = data;
      }
      //console.log(`${fPath} : ${isFile}`);
    }

  }

  public updateVolgNummer(vn: number) {
    let kopConfig = this.kopConfig();
    if (kopConfig != undefined) {
      kopConfig.SetUnlockVolgnr(vn);
    }

  }



  public async compilePCIBBin() {
    if (this.klantNo != undefined) {
      let files: Record<string, any> = {};

      this.addFolder('/working/db/', files);
      this.addFolder('/working/SXB/', files);
      await this.pcibService.init(files);

      let ret = await this.pcibService.compilePCIB(this.klantNo);
      saveLocalFiles(ret, this.wasmModule!);
    }
  }

  public async generateKopList(historyId: string | undefined = undefined) {
    if (this.klantNo != undefined) {
      //let files: Record<string, any> = {};

      let files = await this.retreiveKop(historyId);
      this.addFolder('/working/db/', files);
      //this.addFolder('/working/SXB/', files);

      await this.kopListService.init(files);

      let ret = await this.kopListService.generateList(this.klantNo);
      saveLocalFiles(ret, this.wasmModule!);
      return ret;
    }
  }

  public async compileBin() {
    let wasm = this.wasmModule!;
    if (this.klantNo != undefined) {
      let info = wasm.FS.analyzePath('/working/BIN', undefined);
      if (!info.exists) {
        wasm.FS.mkdir('/working/BIN', undefined);
      }
      let cpmBridge = new wasm.CompBridge(this.klantNo);
      cpmBridge.run();
      var data = this.wasmModule!.FS.readFile(`/working/BIN/${this.klantNo}.BEN`, {
        encoding: 'binary'
      });
      this.electronAPI.saveFile(`BIN/${this.klantNo}.BEN`, data);
      // var dataraw = this.wasmModule!.FS.readFile(`/working/BIN/${this.klantNo}.RAW`, {
      //   encoding: 'binary'
      // });
      // this.electronAPI.saveFile(`BIN/${this.klantNo}.RAW`, dataraw);

    }
  }

  public async unlock() {
    if (this.klantNo != undefined) {
      let wasm = this.wasmModule!;
      let info = wasm.FS.analyzePath('/working/HEX', undefined);
      if (!info.exists) {
        wasm.FS.mkdir('/working/HEX', undefined);
      }
      let cpmBridge = new wasm.CompBridge(this.klantNo);
      cpmBridge.unlock();
      console.log("HERE");
      let t = this.wasmModule!.FS.readdir('/working/HEX/');
      console.log(t);
      var dataraw = this.wasmModule!.FS.readFile(`/working/HEX/${this.klantNo}.HEX`, {
        encoding: 'binary'
      });
      this.electronAPI.saveFile(`HEX/${this.klantNo}.HEX`, dataraw);

    }
  }

  async save(uploadFile: boolean = true) {
    this.jsBridgeSync()!.save();

    let filePath = `SXB/${this.klantNo}.SXB`;
    if (uploadFile) {
      await this.saveFile(filePath);
    } else {
      var data = this.wasmModule!.FS.readFile('/working/' + filePath, {
        encoding: 'binary'
      });
      return data;
    }
  }
  getBridge() {
    return this.jsBridge;
  }

  getBridgeSync() {
    return this.jsBridgeSync()!;
  }

  // getAITypes() {
  //   return this.aiTypes!;
  // }

}
